'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import moment from 'moment';

import Select from '../../../pro/components/Widgets/Select.react';
import UnitsMode from '../../../pro/components/Widgets/UnitsMode.react';
import DateSelector from '../../../pro/components/Widgets/DateSelector.react';
import ConfigWarning from '../../Widgets/ConfigWarning.react';
import Alert from '../../Widgets/Alert/Alert.react';

import energyFormulas from '../../../utils/EnergyCalculator';
import { getPatientDemographic, cmToFeetInches } from '../../../pro/utils/Patients';
import { convertWeight } from '../../../utils/Math';

import allConditions from '../../../tables/conditions';
import conditionsCopy from '../../../tables/conditions-descriptions';

import '../../../pro/components/Modals/Modals.scss';
import '../../MyAccount/PreferencesForm.scss';
import './EstimatorModal.scss';

const mifflin = energyFormulas.filter(f => f.prop === 'mifflin')[0];

const heightFeetOpts = [
    {value: 3, label: '3 ft.'},
    {value: 4, label: '4 ft.'},
    {value: 5, label: '5 ft.'},
    {value: 6, label: '6 ft.'},
    {value: 7, label: '7 ft.'},
    {value: 8, label: '8 ft.'},
];

const heightInchOpts = [
    {value: 0, label: '0 in.'},
    {value: 1, label: '1 in.'},
    {value: 2, label: '2 in.'},
    {value: 3, label: '3 in.'},
    {value: 4, label: '4 in.'},
    {value: 5, label: '5 in.'},
    {value: 6, label: '6 in.'},
    {value: 7, label: '7 in.'},
    {value: 8, label: '8 in.'},
    {value: 9, label: '9 in.'},
    {value: 10, label: '10 in.'},
    {value: 11, label: '11 in.'},
];

export default class EnergyEstimatorModal extends Component {
    static propTypes = {
    };

    static contextTypes = {
        confirm: PropTypes.func,
    };

    constructor(props) {
        super(props);

        let {
            conditions = ['General Healthy Diet'],
            activity_level = '',
            height_cm = '',
            weight_kg = '',
            units_mode = 'english',
            due_date = null,
            fetus_count = null,
            birthdate = null,
            gender = null,
            target_energy_kcal = null,

            mealType = null,
        } = props;

        const height = cmToFeetInches(height_cm || 0);

        if (!['english', 'metric'].includes(units_mode)) {
            units_mode = 'english';
        }

        if (target_energy_kcal == 2000) {
            target_energy_kcal = '';
        }

        this.state = {
            conditions,
            activity_level,
            height_cm,
            weight_kg,
            units_mode,
            due_date,
            fetus_count,
            birthdate,
            gender,
            target_energy_kcal,
            avoidances: [],

            height_feet: height.feet,
            height_inches: height.inches,
            weight_lbs: weight_kg ? convertWeight(weight_kg, 'kg', 'lbs', 0) : '',
            mealType,

            dirty: false,

            isExpanderOpen: false,
            isExpanderOpening: false,
        };
    }

    calc = () => {
        const { conditions, gender, birthdate, weight_kg, height_cm, activity_level, goal_weight_kg } = this.state;

        let age = 32, ageDays = 32 * 365;
        if (!(gender && birthdate && weight_kg && height_cm && activity_level)) {
            return;
        }

        if (moment(birthdate).isValid()) {
            age = moment().diff(birthdate, 'year');
            ageDays = moment().diff(birthdate, 'day');
        }

        const params = {
            gender,
            weightKg: weight_kg,
            heightCm: height_cm,
            activity: activity_level,
            age,
            ageDays,
        };

        let target_energy_kcal = Math.round(mifflin.compute(params));

        if (target_energy_kcal === false) {
            this.setState({target_energy_kcal: '', dirty: true});
            return;
        }

        if (conditions.includes('Pregnancy')) {
            // What trimester are we in, and how many fetus's do we have?
            const { due_date, fetus_count = 1 } = this.state;

            const weeksPregnant = moment().diff(moment(due_date).subtract(42, 'weeks'), 'weeks');

            // First Trimester - NO ADDITIONAL CALORIE NEEDED
            // Second Trimester
            if (weeksPregnant >= 14 && weeksPregnant <= 27) {
                // 340 kcal for the first baby, and 175 kcal for each additional
                target_energy_kcal += 340 + (fetus_count - 1) * 175;
            }

            // Third Trimester
            if (weeksPregnant >= 28) {
                target_energy_kcal += 450 + (fetus_count - 1) * 225;
            }
        } else if (goal_weight_kg) {
            if (goal_weight_kg > weight_kg * 1.025) {
                // Gain weight
                target_energy_kcal += 500;
            } else if (goal_weight_kg < weight_kg * .975) {
                // Lose weight
                target_energy_kcal -= 500;
            }
        }

        if (conditions.includes('Lactation')) {
            target_energy_kcal += 500;
        }

        if (age > 12 && target_energy_kcal < 1200) {
            target_energy_kcal = 1200;
        } else if (age <= 12 && target_energy_kcal < 900) {
            target_energy_kcal = 900;
        }

        this.setState({target_energy_kcal, dirty: true});
    }

    onChangeCondition = (conditionName) => {
        let { gender, avoidances } = this.state;

        const condition = allConditions.filter(condition => condition.name == conditionName)[0];

        if (condition.name === 'Pregnancy' || condition.name === 'Lactation') {
            gender = 'female';
        }

        (condition.avoidances || []).forEach(avoid => {
            if (!avoidances.includes(avoid)) {
                avoidances.push(avoid);
            }
        });

        this.setState({conditions: [condition.name], avoidances, gender, dirty: true}, this.calc);
    }

    toggleUnitsMode = () => {
        let { units_mode } = this.state;
        units_mode = units_mode === 'metric' ? 'english' : 'metric';

        this.setState({units_mode, dirty: true});
    }

    onChangeHeightFeet = (height_feet) => {
        const { height_inches } = this.state;

        const feet = parseInt(height_feet) || 0,
              inches = parseInt(height_inches) || 0;

        const height_cm = Math.round(((feet * 12) + inches) * 2.54) || '';

        this.setState({height_feet, height_cm, dirty: true}, this.calc);
    }

    onChangeHeightInches = (height_inches) => {
        const { height_feet } = this.state;

        const feet = parseInt(height_feet) || 0,
              inches = parseInt(height_inches) || 0;

        const height_cm = Math.round(((feet * 12) + inches) * 2.54) || '';

        this.setState({height_inches, height_cm, dirty: true}, this.calc);
    }

    onChangeHeightCm = (ev) => {
        const height_cm = ev.target.value;

        const english = cmToFeetInches(height_cm) || {feet: '', inches: ''};

        this.setState({height_cm, height_feet: english.feet, height_inches: english.inches}, this.calc);
    }


    onChangeWeightLbs = (ev) => {
        const weight_lbs = ev.target.value;

        const weight_kg = convertWeight(weight_lbs, 'lbs', 'kg', 1) || '';

        this.setState({weight_lbs, weight_kg}, this.calc);
    }

    onChangeWeightKg = (ev) => {
        const weight_kg = ev.target.value;

        const weight_lbs = convertWeight(weight_kg, 'kg', 'lbs', 0) || '';

        this.setState({weight_kg, weight_lbs}, this.calc);
    }

    onChangeActivityLevel = (activity_level) => {
        this.setState({activity_level}, this.calc);
    }

    onChangeGoalWeightLbs = (ev) => {
        let { weight_kg, weekly_goal_kcal } = this.state;
        const goal_weight_lbs = ev.target.value;
        const goal_weight_kg = convertWeight(goal_weight_lbs, 'lbs', 'kg', 0) || '';

        if (goal_weight_kg > weight_kg * 1.025) {
            // Gain weight
            weekly_goal_kcal = 500;
        } else if (goal_weight_kg < weight_kg * .975) {
            // Lose weight
            weekly_goal_kcal = -500;
        } else {
            weekly_goal_kcal = 0;
        }

        this.setState({goal_weight_lbs, goal_weight_kg, weekly_goal_kcal}, this.calc);
    }

    onChangeGoalWeightKg = (ev) => {
        let { weight_kg, weekly_goal_kcal } = this.state;
        const goal_weight_kg = ev.target.value;
        const goal_weight_lbs = convertWeight(goal_weight_kg, 'kg', 'lbs', 0) || '';

        if (goal_weight_kg > weight_kg * 1.025) {
            // Gain weight
            weekly_goal_kcal = 500;
        } else if (goal_weight_kg < weight_kg * .975) {
            // Lose weight
            weekly_goal_kcal = -500;
        } else {
            weekly_goal_kcal = 0;
        }

        this.setState({goal_weight_kg, goal_weight_lbs, weekly_goal_kcal}, this.calc);
    }

    onChangeTargetEnergyKcal = (ev) => {
        const target_energy_kcal = ev.target.value;

        this.setState({target_energy_kcal, dirty: true});
    }

    onChangeDueDate = (due_date) => {
        this.setState({due_date, dirty: true}, this.calc);
    }

    onChangeFetusCount = (fetus_count) => {
        this.setState({fetus_count, dirty: true}, this.calc);
    }

    onChangeGender = (gender) => {
        this.setState({gender, dirty: true}, this.calc);
    }

    onChangeBirthdate = (birthdate) => {
        this.setState({
            birthdate,
            dirty: true,
            age: birthdate ? moment().diff(birthdate, 'years') : false,
        }, this.calc);
    }

    onChangeCalories = (target_energy_kcal) => {
        this.setState({target_energy_kcal});
    }

    validate = () => {
        const { mealType, target_energy_kcal, birthdate, gender, conditions } = this.state;

        if (!conditions.length) {
            return {error: 'conditions', alert: 'Please select a health condition / lifestyle'};
        }

        // Inapproriate condition
        let inapp = false;
        conditions.forEach(name => {
            const condition = allConditions.filter(cd => cd.name === name)[0];

            if (!condition) {
                return;
            }

            const demographic = getPatientDemographic({birthdate, gender}, condition.demographics);

            if (!condition.templates[demographic]) {
                inapp = condition;
            }
        });

        if (inapp) {
            const name = inapp.special_name || inapp.consumer_name || inapp.name;

            return {alert: `We're sorry! ${name} is not compatible with your selection. Please revise to continue.`};
        }

        if (!target_energy_kcal) {
            return {alert: 'Please choose calories per day', error: 'target_energy_kcal'};
        }

        if (!mealType) {
            return {alert: 'Please choose which meal type to calculate nutrition limits for', error: 'mealType'};
        }

        return true;
    }

    apply = () => {
        let alert = false;

        if (true !== (alert = this.validate())) {
            this.setState(alert);
            return;
        }

        const { closeModal, onChangeValues } = this.props;
        const {
            conditions,
            activity_level,
            height_cm,
            weight_kg,
            units_mode,
            due_date,
            fetus_count,
            birthdate,
            gender,
            target_energy_kcal,
            mealType,
        } = this.state;

        onChangeValues({
            conditions,
            activity_level,
            height_cm,
            weight_kg,
            units_mode,
            due_date,
            fetus_count,
            birthdate,
            gender,
            target_energy_kcal,
        }, mealType);

        closeModal();
    }

    getAvoidancesFromConditions = (conditions) => {
        conditions = conditions.map(name => allConditions.filter(cd => cd.name === name)[0]).filter(v => v);

        let avoidances = [];

        conditions.forEach(condition => avoidances = avoidances.concat(condition.avoidances || []));

        // De-duplicate
        avoidances = avoidances.filter((avoidance, i) => avoidances.indexOf(avoidance) === i);

        return avoidances;
    }

    openExpander = () => {
        this.setState({isExpanderOpening: true}, () => {
            setTimeout(() => {
                this.setState({isExpanderOpen: true});
            }, 333);
        })
    }

    render() {
        const { closeModal, conditionsList } = this.props;
        const {
            conditions,
            activity_level,
            height_cm,
            weight_kg,
            units_mode,
            due_date,
            fetus_count,
            birthdate,
            gender,
            target_energy_kcal,

            height_feet,
            height_inches,
            weight_lbs,
            mealType,

            error, alert,
            isExpanderOpen,
            isExpanderOpening,
        } = this.state;


        const selected = allConditions.filter(c => conditions.includes(c.name));

        const copy = selected.length ? conditionsCopy[selected[0].name] : null;
        const conditionsOpts = conditionsList.map(c => ({label: c.name, value: c.name}))
                                             .sort((a, b) => {
            if (a.label === 'General Healthy Diet') return -1;
            if (b.label === 'General Healthy Diet') return 1;

            return a.label.localeCompare(b.label);
        });
        const condition = conditions[0];

        const caloriesOpts = [];
        let calories = 1200;
        for (; calories <= 2800; calories += 100) {
            caloriesOpts.push({label: calories + ' calories', value: calories});
        }

        let age = 32;

        if (moment(birthdate).isValid()) {
            age = moment().diff(birthdate, 'year');
        }

        if (target_energy_kcal && caloriesOpts.filter(co => co.value == target_energy_kcal).length == 0) {
            let label = target_energy_kcal + ' calories ('+ age + ' yr old ' + gender + ')',
                value = target_energy_kcal;

            caloriesOpts.unshift({label, value});
        }

        return (
            <Modal isOpen={true}
                onRequestClose={closeModal}
                closeModal={closeModal}
                contentLabel="Energy Estimator"
                className="el-modal el-modal2"
                overlayClassName="el-modal-overlay">
                <div className="el-modal-container el-modal2-container filter-estimator-container">
                    <header>
                        <h2>Calculate Per-Meal Prescription</h2>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container el-fonts">
                        <ConfigWarning profile={{
                            conditions: conditions.map(name => ({name})),
                            preferences: {
                                diets: [],
                                avoidances: this.getAvoidancesFromConditions(conditions),
                            },
                            birthdate,
                            target_energy_kcal,
                            height_cm,
                            weight_kg,
                        }} />

                        {alert ?
                            <Alert type="error">{alert}</Alert>
                        : null}

                        <div className="el-labeled-input conditions">
                            <label>Health Condition / Lifestyle</label>
                            <Select defaultClassName="el-select-container"
                                options={conditionsOpts}
                                value={condition}
                                onChange={this.onChangeCondition}
                                placeholder="Choose a condition..." />
                        </div>

                        {copy ? <p className="description">{copy.heading}</p> : null}

                        {condition == 'Pregnancy' ?
                            <div className="el-labeled-input due-date">
                                <label>Due Date</label>
                                <DateSelector initialDate={due_date} onChange={this.onChangeDueDate} delta_years={1}
                                    data-error={error === 'due_date'} />
                            </div>
                        : null}

                        {condition == 'Pregnancy' ?
                            <div className="el-labeled-input how-many-babies" data-error={error === 'fetus_count'}>
                                <label>How many fetus?</label>

                                <div className="toggle-container">
                                    <button className="el-flat-pill-toggle-btn male-btn" data-active={fetus_count == 1} onClick={() => this.onChangeFetusCount(1)}>
                                        Single
                                    </button>
                                    <button className="el-flat-pill-toggle-btn female-btn" data-active={fetus_count == 2} onClick={() => this.onChangeFetusCount(2)}>
                                        Twins
                                    </button>
                                </div>
                            </div>
                        : null}

                        {!['Pregnancy', 'Lactation'].includes(condition) ?
                            <div className="el-labeled-input gender">
                                <label>Birth Sex <em>(optional)</em></label>

                                <div className="toggle-container">
                                    <button className="el-flat-pill-toggle-btn el-btn-icon-left female-btn" data-active={gender == 'female'} onClick={() => this.onChangeGender('female')}>
                                        <i className="icon-female-sign" />Female
                                    </button>
                                    <button className="el-flat-pill-toggle-btn el-btn-icon-left male-btn" data-active={gender == 'male'} onClick={() => this.onChangeGender('male')}>
                                        <i className="icon-male-sign" />Male
                                    </button>
                                </div>
                            </div>
                        : null}

                        <div className="collapsible" data-expanding={isExpanderOpening} data-opened={isExpanderOpen}>
                            <DateSelector initialDate={birthdate} onChange={this.onChangeBirthdate}
                                labelClassName="el-labeled-input"
                                selectClassName="el-select-container"
                                data-error={error === 'birthdate'} />

                            <UnitsMode value={units_mode}
                                onClick={this.toggleUnitsMode} />

                            <div className="el-labeled-input height">
                                <label>Height</label>

                                {units_mode === 'english' ?
                                    <Select defaultClassName="el-select-container" className="height-feet"
                                        value={height_feet}
                                        placeholder="feet"
                                        options={heightFeetOpts}
                                        data-error={error === 'height'}
                                        onChange={this.onChangeHeightFeet}>
                                        <p>feet.</p>
                                    </Select>
                                : null}

                                {units_mode === 'english' ?
                                    <Select defaultClassName="el-select-container" className="height-inches"
                                        value={height_inches}
                                        placeholder="inches"
                                        options={heightInchOpts}
                                        data-error={error === 'height'}
                                        onChange={this.onChangeHeightInches}>
                                        <p>inches.</p>
                                    </Select>
                                : null}

                                {units_mode === 'metric' ?
                                    <div className="with-units height-cm">
                                        <input className="pseudo-input" type="number" placeholder="Height"
                                            value={height_cm}
                                            data-error={error === 'height'}
                                            onChange={this.onChangeHeightCm} />

                                        <label>cm.</label>
                                    </div>
                                : null}
                            </div>

                            <div className="el-labeled-input weight">
                                {condition == 'Pregnancy'
                                    ? <label>Pre-Pregnancy Weight</label>
                                    : <label>Current Weight</label>
                                }

                                {units_mode === 'english' ?
                                    <div className="with-units weight-lbs">
                                        <IntegerInput
                                            className="pseudo-input"
                                            value={weight_lbs}
                                            min={0}
                                            max={999}
                                            onChange={(value) => this.onChangeWeightLbs({target: {value}})}
                                        />
                                        <label>Lbs.</label>
                                    </div>
                                : null}

                                {units_mode === 'metric' ?
                                    <div className="with-units weight-kg">
                                        <input className="pseudo-input" type="number"
                                            value={weight_kg}
                                            data-error={error === 'weight'}
                                            onChange={this.onChangeWeightKg} />

                                        <label>Kg.</label>
                                    </div>
                                : null}
                            </div>

                            <div className="el-labeled-input activity-level">
                                <label>Activity Level</label>

                                <Select defaultClassName="el-select-container"
                                    options={mifflin.activity_levels}
                                    value={activity_level} showAbove={true}
                                    data-error={error == 'activity_level'}
                                    placeholder="Activity Level"
                                    onChange={this.onChangeActivityLevel} />
                            </div>
                        </div>

                        <div className="el-labeled-input target-energy-kcal">
                            <label>Calories per Day</label>
                            <Select defaultClassName="el-select-container"
                                options={caloriesOpts}
                                value={target_energy_kcal}
                                showAbove={true}
                                onChange={this.onChangeCalories}
                                placeholder="Select calories per day" />

                            <footer>
                                {!isExpanderOpening ?
                                    <button onClick={this.openExpander} className="calc-energy-btn">
                                        calculate calorie needs
                                    </button>
                                : null}
                            </footer>
                        </div>

                        <section className="el-labeled-input meal-types-container" data-error={error == 'meal-type'}>
                            <label>Calculate nutrient needs for which meal?</label>

                            <div className="toggle-container">
                                <button className="meal-type el-flat-pill-toggle-btn el-btn-icon-left"
                                    data-active={mealType === 'Breakfast'}
                                    onClick={() => this.setState({mealType: 'Breakfast', dirty: true})}>
                                    <i className="icon-breakfast" />
                                    Breakfast
                                </button>

                                <button className="meal-type el-flat-pill-toggle-btn el-btn-icon-left"
                                    data-active={mealType === 'Lunch'}
                                    onClick={() => this.setState({mealType: 'Lunch', dirty: true})}>
                                    <i className="icon-lunch2" />
                                    Lunch
                                </button>

                                <button className="meal-type el-flat-pill-toggle-btn el-btn-icon-left"
                                    data-active={mealType === 'Snack'}
                                    onClick={() => this.setState({mealType: 'Snack', dirty: true})}>
                                    <i className="icon-snacks" />
                                    Snack
                                </button>

                                <button className="meal-type el-flat-pill-toggle-btn el-btn-icon-left"
                                    data-active={mealType === 'Dinner'}
                                    onClick={() => this.setState({mealType: 'Dinner', dirty: true})}>
                                    <i className="icon-dinner" />
                                    Dinner
                                </button>
                            </div>
                        </section>
                    </div>

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={closeModal}>Cancel</button>
                        <button className="el-modal-ok-btn" onClick={this.apply}>Apply</button>
                    </footer>

                </div>
            </Modal>
        );
    }
}
